import React, {useState, useEffect, useRef} from "react"
import styled from "styled-components"
import {useAuth0} from "@auth0/auth0-react"
// redux
import {useDispatch, useSelector} from "react-redux"
import {useForm, Controller} from "react-hook-form"
import {authSelector} from "features/Auth/authSlice"
import {requestAdditionalSessions, fetchUserNotes} from "features/Users/usersSlice"
// UI
import {Container, Row, Col} from "@uprise/grid"
import {Modal} from "@uprise/modal"
import Radio from "components/shared/Radio"
import {TextInputHorizontal} from "@uprise/form"
import {P, Medium} from "@uprise/text"
import {H4, H6} from "@uprise/headings"
import {spacing} from "@uprise/spacing"
import {Button} from "@uprise/button"
import {extended, semantic, primary} from "@uprise/colors"
import {TextArea} from "@uprise/form"

const TextAreaVert = styled(TextArea)`
	resize: vertical;
	margin-bottom: 0;
`

const FormElementContainer = styled.div`
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
	border-bottom: ${props => (props.error ? "1px solid #ff9999" : "1px solid #edeafa")};
`

const FormElementLabel = styled(H6)`
	margin: 16px 0 8px 0;

	&:after {
		content: "${props => (props.isRequired ? "*" : "")}";
	}
`

const FormInputContainer = styled.div`
	flex-basis: 60%;
	position: relative;
	align-self: center;
`

const FormTextInputWithBorder = styled(TextInputHorizontal)`
	border-radius: 4px;
	border: solid 1px #d4cbfb;
	padding: 4px 10px;
`

const ButtonContainer = styled.div`
	display: flex;
`

const PrimaryButton = styled(Button)`
	width: 100%;
	min-width: 100px;
	height: 50px;
	margin: 10px 6px 0;
	outline: none;
`

const MessageStyles = styled.label`
	font-size: 12px;
	color: ${props => {
		if (props.focused) {
			return `${extended.blue.one}`
		} else if (props.validation) {
			return `${semantic.error}`
		} else {
			return `${extended.purple.five}`
		}
	}};
	display: block;
	margin-top: 8px;
	transition: font-size 0.2s;
	text-align: left;
`

const MessageStylesCaps = styled(MessageStyles)`
	padding-left: 40%;
	text-align: left;
`

const ReadOnlyField = styled.div`
	color: #6d6c79;
	text-align: left;
	width: 60%;
`

const ReadOnlyFieldHighlighted = styled(ReadOnlyField)`
	color: ${primary.purple};
`
const FormElementContainerFullWidth = styled.div`
	width: 100%;
	display: flex;
	align-items: center;
	//border-bottom: ${props => (props.error ? "1px solid #ff9999" : "1px solid #edeafa")};
`

const ExtraSessions = ({onCancel, userEmail}) => {
	const dispatch = useDispatch()
	const {getAccessTokenSilently} = useAuth0()

	const {register, control, handleSubmit, watch, setValue, errors, trigger} = useForm({
		// defaultValues: {}; you can populate the fields by this attribute
		mode: "onSubmit",
		reValidateMode: "onChange"
	})
	const [loading, setLoading] = useState(false)
	const [submitModal, setSubmitModal] = useState(false)

	const token = useRef(null)
	const authData = useSelector(authSelector)

	const getToken = () => {
		if (!token.current) {
			return new Promise(async (res, rej) => {
				try {
					const authToken = await getAccessTokenSilently()
					token.current = authToken
					res(authToken)
				} catch (err) {
					console.log(err)
					rej(err)
				}
			})
		} else return Promise.resolve()
	}

	useEffect(() => {
		setValue("sessions", 1, {
			shouldValidate: true
		})
	}, [])

	const handleClose = () => {
		setSubmitModal(false)
	}

	const handleTrigger = async () => {
		const triggered = await trigger()
		if (!triggered) return
		else setSubmitModal(true)
	}

	const watchSession = watch(["sessions"])

	const validateText = text => {
		return !text.trim() ? `Field can not be left empty` : true
	}

	const handleFormSumbit = formData => {
		getToken().then(() => {
			setLoading(true)
			dispatch(
				requestAdditionalSessions({
					data: {
						...formData,
						email: userEmail,
						coachId: authData.data.id,
						title: "How will the user benefit from these extra sessions prior to referring him/her to Medicare or other relevant care services?"
					},
					token: token.current
				})
			)
				.then(() => {})
				.finally(() => {
					setSubmitModal(false)
					setLoading(false)
					onCancel()
				})

			dispatch(fetchUserNotes({userEmail, coachId: authData.data.id, token: token.current})).catch(err =>
				console.log(err)
			)
		})
	}

	return (
		<Container>
			<form>
				<Row>
					<Col>
						<H6 style={{textAlign: "left"}} className='m-t-10 m-b-2'>
							How will the user benefit from these extra sessions prior to referring him/her to Medicare
							or other relevant care services?
						</H6>
					</Col>
					<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
						<Row>
							<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
								<FormElementContainerFullWidth>
									<FormInputContainer style={{flexBasis: "100%"}}>
										<Controller
											name={"note"}
											control={control}
											rules={{
												required: {value: true, message: "Note is required"},
												validate: text => {
													return validateText(text, "Note")
												},
												maxLength: {
													value: 500,
													message: "Field can not exceed 500 characters"
												}
											}}
											defaultValue={""}
											render={props => {
												return (
													<TextAreaVert
														name='note'
														minHeight='200px'
														outline={false}
														//validation={errors}
														value={props.value}
														onChange={event => props.onChange(event.target.value)}
														border={true}
														padding={spacing.s3}
														placeholder='Your note here..'
														id='note'
													/>
												)
											}}
										/>
									</FormInputContainer>
								</FormElementContainerFullWidth>
								{errors["note"] && errors["note"].message ? (
									<MessageStyles htmlFor={1} focused={false} validation={errors["note"]}>
										{errors["note"] && errors["note"].message}
									</MessageStyles>
								) : null}
							</Col>
						</Row>
					</Col>
				</Row>

				<Row>
					<Col>
						<H6 style={{textAlign: "left"}} className='m-t-5 m-b-5'>
							How many sessions would benefit this client before transferring him/her to Medicare or other
							services? *
						</H6>
					</Col>
					<Col className='col-12 col-lg-12 col-md-12 col-sm-12'>
						<FormElementContainer error={errors.coachingType?.type === "required"}>
							<FormInputContainer>
								<Controller
									name={"sessions"}
									control={control}
									rules={{
										required: {
											value: true,
											message: "Session Details is required"
										}
									}}
									defaultValue={1}
									render={props => (
										<>
											<Row>
												<Col className='col-12 col-lg-12 col-md-12 col-sm-12 d-flex m-b-6'>
													<Radio
														checked={watchSession.sessions === 1}
														label={"1 extra session"}
														value={1}
														onChange={e =>
															setValue("sessions", 1, {
																shouldValidate: true
															})
														}
														name={"sessions"}
														width={"250px"}
													/>
												</Col>
												<Col className='col-12 col-lg-12 col-md-12 col-sm-12 d-flex m-b-6'>
													<Radio
														checked={watchSession.sessions === 2}
														label={"2 extra session"}
														value={2}
														onChange={e =>
															setValue("sessions", 2, {
																shouldValidate: true
															})
														}
														name={"sessions"}
														width={"250px"}
													/>
												</Col>
												<Col className='col-12 col-lg-12 col-md-12 col-sm-12 d-flex m-b-6'>
													<Radio
														checked={watchSession.sessions === 3}
														label={"3 extra session"}
														value={3}
														onChange={e =>
															setValue("sessions", 3, {
																shouldValidate: true
															})
														}
														name={"sessions"}
														width={"250px"}
													/>
												</Col>
											</Row>
										</>
									)}
								/>
							</FormInputContainer>
						</FormElementContainer>

						{errors["sessions"] && errors["sessions"].message ? (
							<MessageStyles htmlFor={1} focused={false} validation={errors["sessions"]}>
								{errors["sessions"] && errors["sessions"].message}
							</MessageStyles>
						) : null}
					</Col>
				</Row>

				<Row className='flex-row-reverse'>
					<ButtonContainer>
						<PrimaryButton
							type='button'
							variant={"primary"}
							title={"Send Request"}
							onClick={handleTrigger}
							isLoading={loading}
							style={{paddingRight: 10, paddingLeft: 10}}
						/>
					</ButtonContainer>
				</Row>
			</form>
			<Modal
				isOpen={submitModal}
				handleClose={handleClose}
				padding={"24px"}
				backgroundColor={"#ffffff"}
				width={"800px"}
				scroll={true}
				textAlign='center'
			>
				<Medium className='m-b-5'>{`This request will be sent to the company for approval of extra sessions. Make sure you do NOT include any private or identifiable information.`}</Medium>
				<H4>{`Do you want to continue sending the request?`}</H4>
				<ButtonContainer className='m-t-5'>
					<PrimaryButton
						variant={"primary"}
						title={"No, edit request"}
						style={{paddingRight: 10, paddingLeft: 10}}
						onClick={() => setSubmitModal(false)}
					/>
					<PrimaryButton
						variant={"primary"}
						title={"Yes, send the request"}
						style={{paddingRight: 10, paddingLeft: 10}}
						isLoading={loading}
						onClick={handleSubmit(formData => handleFormSumbit(formData))}
					/>
				</ButtonContainer>
			</Modal>
		</Container>
	)
}

export default ExtraSessions
